import type { GridColumnViewModel } from '@/store/gridColumnsApi';
import type { AdvisorInvestor } from '@/store/usersApi';

/**
 * Fields the Clients widget renders. The full Clients page also offers Status,
 * Insights and the grouping columns, which the widget leaves out because it has
 * no toolbar to configure them.
 */
const WIDGET_COLUMN_FIELDS = [
  'Name',
  'UserName',
  'NetWorth',
  'Home',
  'HomePercentage',
  'LastLogin',
] as const;

const ORION_COLUMN_FIELD = 'LastOrionLogin';

/**
 * Whether at least one loaded client has an Orion login, which is the ticket's
 * condition for showing Last Orion Login. Without one the column does not
 * render at all, and the widget shows seven columns instead of eight.
 */
export const hasOrionClient = (rows: ReadonlyArray<AdvisorInvestor>): boolean =>
  rows.some(
    (row) =>
      typeof row.LastOrionLogin === 'string' && row.LastOrionLogin !== '',
  );

/**
 * Narrows the firm's advisor-clients column configuration to the fields the
 * widget shows. Enablement, labels and order stay the firm's, so the widget and
 * the full page never disagree on a column. `showOrionColumn` is
 * hasOrionClient over the loaded rows.
 */
export const selectDashboardClientColumns = (
  records: GridColumnViewModel[] | undefined,
  showOrionColumn: boolean,
): GridColumnViewModel[] => {
  const allowedFields = new Set<string>(WIDGET_COLUMN_FIELDS);
  if (showOrionColumn) {
    allowedFields.add(ORION_COLUMN_FIELD);
  }

  return (records ?? []).filter((record) =>
    allowedFields.has(record.GridColumnDataField),
  );
};
